"use client"

import {
  classifyMarketingChannel,
  getReferrerHost,
  mergeUtmForEvent,
  readWsaAttributionFromDocumentCookie,
  utmParamsFromSearchParams,
} from "@webscraping/attribution"
import { usePathname, useSearchParams } from "next/navigation"
import { usePostHog } from "posthog-js/react"
import { useEffect } from "react"
import { isDev } from "~/env"

/**
 * Marketing-funnel page view tracker. Fires `marketing.page_viewed` (in
 * addition to PostHog's auto `$pageview`) with `path`, referrer/channel props,
 * and merged UTM properties from the URL + the `wsa_attribution` cookie set by
 * `apps/web/proxy.ts`. F1 acquisition funnel keys on this event so it can be
 * filtered to marketing surfaces only (the (web) route group).
 *
 * Per `09-event-taxonomy.md` Section 8.2: when the URL carries a non-empty
 * UTM, the same UTM is mirrored to person-level `last_utm_*` via `$set` so
 * the most recent attribution touch is queryable as a person property.
 *
 * Dev mode skip mirrors `useTrackEvent` so local navigation does not pollute
 * the production PostHog project.
 */
export function PostHogPageView() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const posthog = usePostHog()

  useEffect(() => {
    if (!posthog || !pathname) return
    if (isDev) {
      console.log("Tracking event: marketing.page_viewed", { path: pathname })
      return
    }
    try {
      const fromUrl = utmParamsFromSearchParams(searchParams)
      const fromCookie = readWsaAttributionFromDocumentCookie()
      const utm = mergeUtmForEvent(fromUrl, fromCookie)
      const referrer = typeof document === "undefined" ? null : document.referrer || null
      const referrerHost = getReferrerHost(referrer)
      const channelGroup = classifyMarketingChannel({
        utmMedium: utm.utm_medium,
        utmSource: utm.utm_source,
        utmCampaign: utm.utm_campaign,
        referrer,
      })
      const hasFreshUtm = Object.values(fromUrl).some(v => typeof v === "string" && v.length > 0)
      const lastUtmSet = hasFreshUtm
        ? Object.fromEntries(Object.entries(fromUrl).map(([k, v]) => [`last_${k}`, v]))
        : undefined
      posthog.capture("marketing.page_viewed", {
        path: pathname,
        referrer,
        referrer_host: referrerHost,
        channel_group: channelGroup,
        ...utm,
        ...(lastUtmSet ? { $set: lastUtmSet } : {}),
      })
    } catch (error) {
      console.error("[posthog] failed to capture marketing.page_viewed", error)
    }
  }, [pathname, searchParams, posthog])

  return null
}
